import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Trophy, Users, UserSquare2, CalendarDays, MapPin, Play, ArrowRight } from "lucide-react";

function StatCard({ icon: Icon, label, value, to }) {
  return (
    <Link to={to} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-navy-deep border border-navy-border rounded-sm p-6 hover:border-orange transition-all group">
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-sm bg-orange/15 grid place-items-center text-orange group-hover:bg-orange group-hover:text-white transition-colors">
          <Icon size={20} />
        </div>
        <ArrowRight size={16} className="text-ink-muted group-hover:text-orange group-hover:translate-x-1 transition-all" />
      </div>
      <div className="mt-5 font-mono tabular text-4xl font-black text-white">{value}</div>
      <div className="text-xs font-heading uppercase tracking-widest text-ink-secondary mt-1">{label}</div>
    </Link>
  );
}

export default function AdminDashboard() {
  const [counts, setCounts] = useState({ t: 0, te: 0, p: 0, v: 0, g: 0 });
  const [liveGames, setLiveGames] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/tournaments"),
      api.get("/teams"),
      api.get("/players"),
      api.get("/venues"),
      api.get("/games"),
    ]).then(([t, te, p, v, g]) => {
      setCounts({ t: t.data.length, te: te.data.length, p: p.data.length, v: v.data.length, g: g.data.length });
      setLiveGames(g.data.filter((x) => x.status === "live" || x.status === "overtime"));
      setRecent(g.data.slice(0, 8));
    });
  }, []);

  return (
    <div className="p-6 md:p-10">
      <PageHeader title="Dashboard" subtitle="Welcome to the GPS STATS control room" testid="page-admin-dashboard" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard icon={Trophy} label="Tournaments" value={counts.t} to="/admin/tournaments" />
        <StatCard icon={Users} label="Teams" value={counts.te} to="/admin/teams" />
        <StatCard icon={UserSquare2} label="Players" value={counts.p} to="/admin/players" />
        <StatCard icon={MapPin} label="Venues" value={counts.v} to="/admin/venues" />
        <StatCard icon={CalendarDays} label="Games" value={counts.g} to="/admin/games" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <section className="bg-navy-deep border border-navy-border rounded-sm overflow-hidden">
          <div className="px-5 py-3 bg-navy-surface border-b border-navy-border flex items-center justify-between">
            <h3 className="font-heading uppercase tracking-widest text-orange text-sm flex items-center gap-2">
              <span className="w-2 h-2 bg-court-red rounded-full animate-pulse" /> Live now
            </h3>
            <span className="text-xs font-mono text-ink-muted">{liveGames.length} active</span>
          </div>
          {liveGames.length === 0 ? (
            <div className="p-8 text-center text-ink-muted text-sm">No games are currently live. Start one from Games.</div>
          ) : (
            <div className="divide-y divide-navy-border">
              {liveGames.map((g) => (
                <Link key={g.id} to={`/operator/${g.id}`} className="flex items-center justify-between px-5 py-4 hover:bg-navy-surface/40">
                  <div className="font-heading uppercase tracking-tight">Game {g.id.slice(0, 8)}</div>
                  <div className="font-mono tabular text-2xl text-orange">{g.score_a} – {g.score_b}</div>
                  <span className="text-xs uppercase font-heading tracking-widest text-orange flex items-center gap-1"><Play size={12}/> Operate</span>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="bg-navy-deep border border-navy-border rounded-sm overflow-hidden">
          <div className="px-5 py-3 bg-navy-surface border-b border-navy-border flex items-center justify-between">
            <h3 className="font-heading uppercase tracking-widest text-orange text-sm">Latest games</h3>
            <Link to="/admin/games" className="text-xs uppercase font-heading tracking-widest text-ink-secondary hover:text-orange">All →</Link>
          </div>
          {recent.length === 0 ? (
            <div className="p-8 text-center text-ink-muted text-sm">No games yet.</div>
          ) : (
            <div className="divide-y divide-navy-border">
              {recent.map((g) => (
                <Link key={g.id} to={`/admin/games`} className="flex items-center justify-between px-5 py-3 hover:bg-navy-surface/40">
                  <div>
                    <div className="font-heading uppercase tracking-tight text-sm">Game {g.id.slice(0, 6)}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{g.date} · {g.status}</div>
                  </div>
                  <div className="font-mono tabular text-ink-secondary">{g.score_a} – {g.score_b}</div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
